<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-medium text-gray-900">Animation</h3>
      <div class="flex items-center">
        <label for="animation-toggle" class="text-sm text-gray-700 mr-2">
          {{ animationEnabled ? 'Enabled' : 'Disabled' }}
        </label>
        <button 
          id="animation-toggle" 
          class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          :class="animationEnabled ? 'bg-indigo-600' : 'bg-gray-200'"
          role="switch"
          :aria-checked="animationEnabled"
          @click="toggleAnimation"
        >
          <span 
            :class="animationEnabled ? 'translate-x-5' : 'translate-x-0'" 
            class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
          />
        </button>
      </div>
    </div>
    
    <div v-if="animationEnabled" class="space-y-4">
      <div>
        <label for="animation-type" class="block text-sm font-medium text-gray-700 mb-1">
          Animation Type
        </label>
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          <button
            v-for="type in animationTypes"
            :key="type.value"
            class="px-3 py-2 text-sm border rounded-md transition-colors"
            :class="animationType === type.value ? 'bg-indigo-100 border-indigo-500 text-indigo-800' : 'border-gray-300 hover:bg-gray-50'"
            @click="setAnimationType(type.value)"
          >
            {{ type.label }}
          </button>
        </div>
      </div>
      
      <div>
        <label for="animation-speed" class="block text-sm font-medium text-gray-700 mb-1">
          Animation Speed
        </label>
        <div class="grid grid-cols-3 gap-2">
          <button
            v-for="speed in animationSpeeds"
            :key="speed.value"
            class="px-3 py-2 text-sm border rounded-md transition-colors"
            :class="animationSpeed === speed.value ? 'bg-indigo-100 border-indigo-500 text-indigo-800' : 'border-gray-300 hover:bg-gray-50'"
            @click="setAnimationSpeed(speed.value)"
          >
            {{ speed.label }}
          </button>
        </div>
      </div>
      
      <div>
        <div class="flex justify-between items-center">
          <label for="gif-toggle" class="text-sm font-medium text-gray-700">
            GIF Mode
          </label>
          <div class="flex items-center">
            <span class="text-sm text-gray-500 mr-2">
              {{ isGifMode ? 'Enabled' : 'Disabled' }}
            </span>
            <button 
              id="gif-toggle" 
              class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              :class="isGifMode ? 'bg-indigo-600' : 'bg-gray-200'"
              role="switch"
              :aria-checked="isGifMode"
              @click="toggleGifMode"
            >
              <span 
                :class="isGifMode ? 'translate-x-5' : 'translate-x-0'" 
                class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
              />
            </button>
          </div>
        </div>
        <p class="mt-1 text-sm text-gray-500">
          Enable GIF mode to create animated GIFs with your chosen animation style
        </p>
      </div>
      
      <div v-if="isGifMode" class="mt-4">
        <button
          class="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          :disabled="previewLoading"
          @click="previewAnimation"
        >
          <span v-if="previewLoading" class="flex items-center justify-center">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Generating Preview...
          </span>
          <span v-else>Preview Animation</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

export default {
  name: 'AnimationOptions',
  data() {
    return {
      animationTypes: [
        { value: 'bounce', label: 'Bounce' },
        { value: 'fade', label: 'Fade' },
        { value: 'rotate', label: 'Rotate' },
        { value: 'pulse', label: 'Pulse' },
        { value: 'typewriter', label: 'Typewriter' }
      ],
      animationSpeeds: [
        { value: 'slow', label: 'Slow' },
        { value: 'medium', label: 'Medium' },
        { value: 'fast', label: 'Fast' }
      ],
      previewLoading: false
    };
  },
  computed: {
    ...mapState({
      animationEnabled: state => state.overlaySettings.animation.enabled,
      animationType: state => state.overlaySettings.animation.animationType,
      animationSpeed: state => state.overlaySettings.animation.speed,
      isGifMode: state => state.isGifMode
    })
  },
  methods: {
    ...mapMutations([
      'UPDATE_ANIMATION_ENABLED',
      'UPDATE_ANIMATION_TYPE',
      'UPDATE_ANIMATION_SPEED',
      'SET_IS_GIF_MODE',
      'SET_SHOULD_PREVIEW_GIF'
    ]),
    
    toggleAnimation() {
      this.UPDATE_ANIMATION_ENABLED(!this.animationEnabled);
    },
    
    setAnimationType(type) {
      this.UPDATE_ANIMATION_TYPE(type);
    },
    
    setAnimationSpeed(speed) {
      this.UPDATE_ANIMATION_SPEED(speed);
    },
    
    toggleGifMode() {
      this.SET_IS_GIF_MODE(!this.isGifMode);
    },
    
    previewAnimation() {
      this.previewLoading = true;
      
      // Tell the store to preview the GIF
      this.SET_SHOULD_PREVIEW_GIF(true);
      
      // Reset the loading state after a timeout
      // In a real app, this would be based on when the GIF is actually loaded
      setTimeout(() => {
        this.previewLoading = false;
      }, 1500);
    }
  }
};
</script>